import { useEffect, useMemo, useState, useRef } from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import { useServices, useServiceOfferings } from '@/hooks/useServices'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import './Steps.css'

type ServicesStepProps = {
    initialSelected?: number[]
    onSelectionChange?: (serviceIds: number[]) => void
    onComplete?: () => void
}

type Service = {
    id: number
    name: string
    slug: string
    category_name: string
    description: string
    pricing_type: 'hourly' | 'quote'
}

const ServicesStep = ({ initialSelected = [], onSelectionChange, onComplete }: ServicesStepProps) => {
    const { data: servicesData, isLoading: servicesLoading } = useServices(150)
    const { data: offeringsData, isLoading: offeringsLoading } = useServiceOfferings()

    const [selectedServices, setSelectedServices] = useState<Set<number>>(new Set(initialSelected))
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const hasInitialized = useRef(false)

    // Format category name from snake_case/UPPER_SNAKE_CASE to Title Case
    const formatCategoryName = (categoryName: string): string => {
        return categoryName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
    }

    const services = useMemo<Service[]>(() => servicesData?.results || [], [servicesData])
    const loading = servicesLoading || offeringsLoading

    // Initialize selected services only once from offerings or initialSelected
    useEffect(() => {
        // Only initialize once
        if (hasInitialized.current) return

        if (!offeringsData || !Array.isArray(offeringsData) || offeringsData.length === 0) {
            if (initialSelected.length > 0) {
                setSelectedServices(new Set(initialSelected))
                hasInitialized.current = true
            }
            return
        }
        
        const defaults = new Set<number>()
        offeringsData.forEach((offering: any) => {
            const serviceId = typeof offering.service === 'number' ? offering.service : offering.service?.id
            if (typeof serviceId === 'number') {
                defaults.add(serviceId)
            }
        })
        
        if (defaults.size > 0) {
            setSelectedServices(defaults)
            hasInitialized.current = true
        } else if (initialSelected.length > 0) {
            setSelectedServices(new Set(initialSelected))
            hasInitialized.current = true
        }
    }, [offeringsData, initialSelected]) // Only initialize when data sources change

    useEffect(() => {
        onSelectionChange?.(Array.from(selectedServices))
    }, [onSelectionChange, selectedServices])

    const groupedServices = useMemo<Record<string, Service[]>>(() => {
        return services.reduce((acc, service) => {
            const category = service.category_name || 'Otros'
            if (!acc[category]) {
                acc[category] = []
            }
            acc[category].push(service)
            return acc
        }, {} as Record<string, Service[]>)
    }, [services])

    // Initialize selected category when services load
    useEffect(() => {
        const categoryNames = Object.keys(groupedServices)
        if (!categoryNames.length) return

        // If no category is selected, select the first one
        if (!selectedCategory && categoryNames.length > 0) {
            setSelectedCategory(categoryNames[0])
        }
    }, [groupedServices, selectedCategory])

    const toggleService = (serviceId: number) => {
        setSelectedServices(prev => {
            const next = new Set(prev)
            if (next.has(serviceId)) {
                next.delete(serviceId)
            } else {
                next.add(serviceId)
            }
            return next
        })
    }

    const handleContinue = () => {
        if (selectedServices.size === 0) {
            alert('Selecciona al menos un servicio para continuar.')
            return
        }
        onComplete?.()
    }

    if (loading) {
        return (
            <div className="step-container">
                <div className="step-loading" style={{ padding: '3rem 0' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem' }}>Cargando servicios disponibles…</p>
                </div>
            </div>
        )
    }

    const selectedCount = selectedServices.size

    return (
        <div className="step-container">
            <div className="step-section">
                <h4 className="step-section-title">Selecciona tus servicios</h4>
                <div className="step-pricing-explanation">
                    <p className="step-hint">
                        Marca los servicios que ofreces. En el siguiente paso podrás definir tarifas, notas y detalles.
                    </p>
                    <div className="step-pricing-types">
                        <div className="step-pricing-type-item">
                            <span className="step-pricing-pill step-pricing-pill--hourly">Cobro por hora</span>
                            <span className="step-pricing-type-desc">Servicio con tarifa por hora (ej: profesor, entrenador)</span>
                        </div>
                        <div className="step-pricing-type-item">
                            <span className="step-pricing-pill step-pricing-pill--quote">Cotización</span>
                            <span className="step-pricing-type-desc">El precio se define dependiendo de lo que el usuario requiera.</span>
                        </div>
                    </div>
                </div>

                <div className="step-selection-summary">
                    <span className="step-selection-pill">
                        {selectedCount} servicio{selectedCount === 1 ? '' : 's'} seleccionado{selectedCount === 1 ? '' : 's'}
                    </span>
                </div>

                <div className="step-categories-grid">
                    {(Object.keys(groupedServices) as string[]).map((category) => {
                        const categoryServices = groupedServices[category]
                        const selectedInCategory = categoryServices.filter(service => selectedServices.has(service.id)).length
                        const isSelected = selectedCategory === category

                        return (
                            <button
                                key={category}
                                type="button"
                                className={`step-category-chip ${isSelected ? 'step-category-chip--selected' : ''}`}
                                onClick={() => setSelectedCategory(category)}
                            >
                                <span className="step-category-chip-name">{formatCategoryName(category)}</span>
                                <span className="step-category-chip-count">
                                    {selectedInCategory}/{categoryServices.length}
                                </span>
                            </button>
                        )
                    })}
                </div>

                {selectedCategory && groupedServices[selectedCategory] && (
                    <div className="step-category-services">
                        <h5 className="step-category-services-title">
                            Servicios en {formatCategoryName(selectedCategory)}
                        </h5>
                        <div className="step-category-services-list">
                            {groupedServices[selectedCategory].map((service) => {
                                const isSelected = selectedServices.has(service.id)
                                return (
                                    <div
                                        key={service.id}
                                        className={`step-service-item ${isSelected ? 'step-service-item--selected' : ''}`}
                                        onClick={() => toggleService(service.id)}
                                    >
                                        <div className="step-service-info">
                                            <div className="step-service-name-desc">
                                                <span className="step-service-name">{service.name}</span>
                                                {service.description && (
                                                    <span className="step-service-desc">{service.description}</span>
                                                )}
                                                <span className={`step-pricing-pill step-pricing-pill--${service.pricing_type}`}>
                                                    {service.pricing_type === 'hourly' ? 'Cobro por hora' : 'Cotización'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={`step-service-checkbox ${isSelected ? 'step-service-checkbox--checked' : ''}`}>
                                            {isSelected && <FaCheckCircle />}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            <div className="step-actions">
                <Button
                    variant="primary"
                    size="lg"
                    onClick={handleContinue}
                >
                    {selectedCount > 0 ? 'Continuar con la configuración' : 'Seleccionar servicios'}
                </Button>
            </div>
        </div>
    )
}

export default ServicesStep
